
import { Component, Vue } from 'vue-property-decorator'
import {
  getRechargeBonusRuleList,
  createRechargeBonusRule,
  updateRechargeBonusRule,
  deleteRechargeBonusRule,
  setRechargeBonusRuleEnabled,
  rechargeBonusRuleSetSort,
  IRechargeBonusRule
} from '@/api/rechargeRule'
import { UserModule } from '@/store/modules/user'
import { listMyStores } from '@/api/store'
import SortInlineEdit from '@/components/SortInlineEdit/index.vue'
import { scheduleElTableFixedSync } from '@/utils/elTableFixedSync'

@Component({
  name: 'RechargeRule',
  components: { SortInlineEdit }
})
export default class extends Vue {
  private ruleList: IRechargeBonusRule[] = []

  private ruleDialogVisible: boolean = false

  private ruleForm: any = {
    id: undefined,
    storeId: null,
    thresholdAmount: '',
    bonusAmount: '',
    sort: 0,
    enabled: 1
  }

  private storeOptions: Array<{ id: number; name: string }> = []

  mounted() {
    this.loadStores()
    this.loadRuleList()
    window.addEventListener('resize', this.scheduleTableLayout)
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.scheduleTableLayout)
  }

  private scheduleTableLayout() {
    scheduleElTableFixedSync(this, 'mainTable')
  }

  private getCurrentStoreId() {
    const sid = UserModule.storeId
    if (!sid) return null
    const n = Number(sid)
    return Number.isNaN(n) ? null : n
  }

  private get currentStoreLabel(): string {
    const sid = this.getCurrentStoreId()
    if (sid == null) return '未选择商户'
    const found = this.storeOptions.find((s) => Number(s.id) === sid)
    return found ? found.name : `商户${sid}`
  }

  private async loadRuleList() {
    const currentStoreId = this.getCurrentStoreId()
    if (!currentStoreId) {
      this.ruleList = []
      return
    }
    const res: any = await getRechargeBonusRuleList({ storeId: currentStoreId })
    if (String(res.data.code) === '1' && Array.isArray(res.data.data)) {
      this.ruleList = res.data.data.filter(
        (row: IRechargeBonusRule) =>
          row && row.storeId != null && Number(row.storeId) === currentStoreId
      )
    } else {
      this.ruleList = []
    }
    this.scheduleTableLayout()
  }

  private async loadStores() {
    try {
      const res: any = await listMyStores()
      if (String(res.data.code) === '1' && Array.isArray(res.data.data)) {
        this.storeOptions = res.data.data.map((it: any) => ({
          id: it.id,
          name: it.storeName || it.name || `商户${it.id}`
        }))
      } else {
        this.storeOptions = []
      }
    } catch (e) {
      this.storeOptions = []
    }
  }

  private getStoreName(id: number) {
    const found = this.storeOptions.find(s => s.id === id)
    return found ? found.name : `商户${id}`
  }

  private setSortHandle(row: any, sort: number) {
    if (!row || !row.id) return
    rechargeBonusRuleSetSort({ id: row.id, sort })
      .then((res: any) => {
        if (res && res.data && String(res.data.code) === '1') {
          this.$message.success('排序已更新')
          this.loadRuleList()
        } else {
          this.$message.error((res.data && (res.data.msg || res.data.desc)) || '排序失败')
        }
      })
      .catch((err: any) => {
        this.$message.error('请求出错了：' + ((err && err.message) || '未知错误'))
      })
  }

  private openRuleDialog(row?: IRechargeBonusRule) {
    const storeId = this.getCurrentStoreId()
    if (!storeId) {
      this.$message.warning('请先在顶栏选择商户')
      return
    }
    if (row) {
      this.ruleForm = {
        id: row.id,
        storeId,
        thresholdAmount: String(row.thresholdAmount),
        bonusAmount: String(row.bonusAmount),
        sort: row.sort || 0,
        enabled: row.enabled != null ? row.enabled : 1
      }
    } else {
      this.ruleForm = {
        id: undefined,
        storeId,
        thresholdAmount: '',
        bonusAmount: '',
        sort: 0,
        enabled: 1
      }
    }
    this.ruleDialogVisible = true
  }

  private async saveRule() {
    const storeId = this.getCurrentStoreId()
    if (!storeId) {
      this.$message.warning('请先在顶栏选择商户')
      return
    }
    const payload: IRechargeBonusRule = {
      id: this.ruleForm.id,
      storeId,
      thresholdAmount: Number(this.ruleForm.thresholdAmount),
      bonusAmount: Number(this.ruleForm.bonusAmount),
      sort: this.ruleForm.id ? Number(this.ruleForm.sort || 0) : (null as any),
      enabled: this.ruleForm.enabled
    }
    if (payload.id) {
      await updateRechargeBonusRule(payload)
    } else {
      await createRechargeBonusRule(payload)
    }
    this.$message.success('保存成功')
    this.ruleDialogVisible = false
    this.loadRuleList()
  }

  private async toggleRule(row: IRechargeBonusRule) {
    const enabled = row.enabled === 1 ? 0 : 1
    await setRechargeBonusRuleEnabled(row.id as number, enabled)
    this.$message.success('操作成功')
    this.loadRuleList()
  }

  private async deleteRule(row: IRechargeBonusRule) {
    try {
      await this.$confirm('确定删除该规则吗？', '提示', { type: 'warning' })
    } catch {
      return
    }
    await deleteRechargeBonusRule(row.id as number)
    this.$message.success('删除成功')
    this.loadRuleList()
  }

}
